import { Box, IconButton, Typography } from '@mui/material';

import ForecasterProfile from 'components/ForecasterProfile';
import PadlockIcon from 'components/Icons/Padlock';
import SubregionForecastLink from 'components/SubregionForecastLink';
import config from 'config';
import type { Forecaster } from 'types/subregion';

import styles from './ForecastDayContent.module.scss';

const upgradeUrl = `${config.surflineHost}${config.funnelUrl}`;

interface Props {
  conditions: any;
  dayIndex: number;
  forecastDate: string;
  forecastNote?: string | null;
  forecaster?: Forecaster | null;
  headline: string;
  isPaywallActive?: boolean;
  lastPublished?: number;
  observation: string;
  spotId: string;
  spotName: string;
  subregionId: string;
  subregionName: string;
  title?: string;
  titleSuffix?: React.ReactNode;
  type: 'graph' | 'table';
  utcOffset: number;
}

const ForecastDayContent: React.FC<Props> = ({
  conditions,
  dayIndex,
  forecastDate,
  forecastNote,
  forecaster,
  headline,
  isPaywallActive,
  lastPublished,
  observation,
  spotId,
  subregionId,
  subregionName,
  title,
  titleSuffix,
  utcOffset,
}) => {
  return (
    <Box className={styles.wrapper} data-testid="forecast-day-content">
      <Box className={styles.header}>
        {forecaster && (
          <Box>
            <Box className={styles.meta}>
              <ForecasterProfile
                alt={`${subregionName} surf forecast by ${forecaster.name}`}
                conditions={conditions}
                lastUpdate={
                  lastPublished
                    ? {
                        timestamp: lastPublished,
                        utcOffset,
                      }
                    : undefined
                }
                name={forecaster.name}
                iconUrl={forecaster.avatarUrl}
                isShowConditionsBorder={!!conditions}
                title={title}
                titleSuffix={titleSuffix}
              />
            </Box>
          </Box>
        )}
        {isPaywallActive && dayIndex !== 0 && (
          <IconButton
            className={styles.paywallLockButton}
            data-testid="close-button"
            disableFocusRipple
            disableRipple
            disableTouchRipple
            href={upgradeUrl}
          >
            <PadlockIcon locked />
          </IconButton>
        )}
        {!isPaywallActive && (
          <SubregionForecastLink
            contentBlock={`Day ${dayIndex}`}
            forecasterName={forecaster?.name}
            pageName="Spot Report"
            spotId={spotId}
            start={forecastDate}
            subregionId={subregionId}
            subregionName={subregionName}
          />
        )}
      </Box>
      {(!isPaywallActive || dayIndex === 0) && (
        <Box className={styles.body}>
          <Typography variant="footnote" component="p" py={2}>
            {subregionName}
          </Typography>
          <Typography variant="footnote" component="p" className={styles.headline}>
            {headline}
          </Typography>
          <Typography variant="footnote" component="p" className={styles.observation}>
            <span
              dangerouslySetInnerHTML={{
                __html: observation,
              }}
            />
          </Typography>
          {forecastNote && (
            <Typography variant="footnote" component="div" className={styles.note} mt={2}>
              <div dangerouslySetInnerHTML={{ __html: forecastNote }} />
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ForecastDayContent;
